import { Recommendation } from '../../types';
import { logger } from '../logger';
import { areSimilarCategories, areComplementaryCategories } from './categories/similarity';
import { MAX_RECOMMENDATIONS } from './constants';

const MIN_COUNT = 3;
const MAX_COUNT = MAX_RECOMMENDATIONS;
const QUALITY_THRESHOLD = 25;
const SCORE_DROP_THRESHOLD = 0.6;

export function applyDiversityFilter(recommendations: Recommendation[]): Recommendation[] {
  logger.info('Starting diversity filter', {
    inputCount: recommendations.length,
    categories: [...new Set(recommendations.map(r => r.tool.category))]
  });

  const sortedRecs = [...recommendations].sort((a, b) => b.score - a.score);

  if (sortedRecs.length < MIN_COUNT) {
    return sortedRecs;
  }

  const result: Recommendation[] = [];

  if (sortedRecs.length > 0) {
    result.push(sortedRecs[0]);
  }

  for (const rec of sortedRecs.slice(1)) {
    if (result.length >= MAX_COUNT) break;

    if (result.some(r => r.tool.id === rec.tool.id)) continue;

    if (result.some(r => areSimilarCategories(r.tool.category, rec.tool.category))) {
      continue;
    }

    const complementaryBoost = result.some(r =>
      areComplementaryCategories(r.tool.category, rec.tool.category)
    );

    if (complementaryBoost) {
      logger.info('Complementary category match', {
        name: rec.tool.name,
        category: rec.tool.category,
        complementsWith: result
          .filter(r => areComplementaryCategories(r.tool.category, rec.tool.category))
          .map(r => r.tool.category)
      });
    }

    result.push(rec);
  }

  while (result.length < MIN_COUNT) {
    const unusedTool = sortedRecs.find(rec =>
      !result.some(r => r.tool.id === rec.tool.id) &&
      !result.some(r => areSimilarCategories(r.tool.category, rec.tool.category))
    );

    if (unusedTool) {
      result.push({ ...unusedTool, reasons: unusedTool.reasons.slice(0, 3) });
    } else {
      break;
    }
  }

  const finalResult = determineDynamicCount(result);

  logger.info('Final recommendations', {
    count: finalResult.length,
    categories: finalResult.map(r => r.tool.category)
  });

  return finalResult;
}

function determineDynamicCount(recommendations: Recommendation[]): Recommendation[] {
  if (recommendations.length <= MIN_COUNT) {
    return recommendations;
  }

  const topThree = recommendations.slice(0, 3);
  const fourth = recommendations[3];

  if (!fourth) {
    return topThree;
  }

  const thirdScore = topThree[2].matchScore;
  const fourthScore = fourth.matchScore;

  if (fourthScore < QUALITY_THRESHOLD) {
    return topThree;
  }

  if (thirdScore > 0 && fourthScore / thirdScore < SCORE_DROP_THRESHOLD) {
    return topThree;
  }

  return recommendations.slice(0, MAX_COUNT);
}
